import React from 'react'
import memoizee from 'memoizee'
import {
  formatToLocalDate,
  toLocaleNonAmericanDateString,
  formatShortLocalDateStringToDate,
  getWeekNumber,
  getDateOfISOWeek,
} from './../../../shared/libraries/utils/date'
import { compareStringSimilarity } from './../../../Utils/Filters'
import { downloadStringAsFile } from './../../../Utils/Misc'
import { createLowerCaseHashMapFromArray } from './../../../Utils/Array'
import { isDollarsAdjustment } from './../../../Utils/Validators/csv'
import { authorization } from '../../../Module/Auth/Auth'
import {
  getCampaignsLink,
  getCampaignLink,
  getCityPlanLink,
} from '../../../Utils/Link'
import { PATHS } from '../../../Config/Routing/paths'
import CampaignsIcon from '../../../Assets/Images/icons/campaigns'
import CitiesIcon from '../../../Assets/Images/icons/cities'
import { groupMissionsByWeek } from '../../../Utils/Mission'
import { normalizeGraphQLConnection } from '../../../shared/graphql/utils'

const createHash = memoizee(createLowerCaseHashMapFromArray)

export const convertCreateMissionDtoToMission = (d, { locations }) => {
  var locationsHash = {}
  locations.forEach(l => (locationsHash[l.name.toLowerCase()] = l.id))
  var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])

  return {
    locationId: locationsHash[d['LOCATION'].toLowerCase()],
    locationName: d['LOCATION'],
    dateTime: formatToLocalDate(dateTime),
    draftPayAdjustment: parseFloat(d['PAY_ADJUSTMENT']),
  }
}

/**
 * Get a Date object from the csv provided date and time strings.
 * @param {string} dateStr
 * @param {string} timeStr
 * @returns {Date}
 */
export const getDateFromStrings = function(dateStr, timeStr) {
  var dateTime
  if (/\d{4}-\d{2}-\d{2}/.test(dateStr)) dateTime = new Date(dateStr)
  else dateTime = formatShortLocalDateStringToDate(dateStr)
  // TODO Validate the format of the fields
  var [hours, minutes] = timeStr.split(':')
  dateTime.setHours(Number(hours))
  dateTime.setMinutes(Number(minutes))
  return dateTime
}

/**
 * Find the closest matching string in an array of candidates
 * @param {string} str
 * @param {Array<string>} candidates
 * @return {string}
 */
const findSimilarString = function(str, candidates) {
  let max = 0
  return candidates.reduce((agg, candidate) => {
    const delta = compareStringSimilarity(candidate, str)
    if (delta > max) {
      max = delta
      return candidate
    }

    return agg
  }, '')
}

export const maybeGetSuggestionText = function(str, candidates) {
  var suggestionStr = findSimilarString(str, candidates)
  if (suggestionStr) return `Did you mean '${suggestionStr}'?`
  return
}

/**
 * Confirms that the raw spreadsheet data is in the correct format. And there is
 * the required data to create a new mission. Will throw on the first
 * encountered error.
 * @param {object} d An object representing the csv imported row
 */
export const validateCreateMissionDto = (
  d,
  { locationNames, dateRange, now },
) => {
  // Allow YYYY-MM-DD or DD/MM/YYYY or D/M/YYYY or DD/MM/YY
  if (
    !/\d{4}-\d{2}-\d{2}/.test(d['DATE']) &&
    !/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(d['DATE'])
  )
    throw new Error('Date does not match expected format DD/MM/YYYY.')

  if (!/\d{1,2}:\d{2}/.test(d['START_TIME']))
    throw new Error('Start time does not match expected format HH:MM.')

  if (!d['LOCATION']) throw new Error('Missing location.')

  if (
    d['PAY_ADJUSTMENT'] &&
    d['PAY_ADJUSTMENT'].length &&
    !isDollarsAdjustment(d['PAY_ADJUSTMENT'])
  )
    throw new Error(
      'Payout is not valid, expects a dollar amount with cents as two decimal places. e.g. 57.00',
    )

  var locationsHash = createHash(locationNames)
  if (!locationsHash[d['LOCATION'].toLowerCase()]) {
    var error = new Error(`Location '${d['LOCATION']}' not found.`)
    error.suggestion = maybeGetSuggestionText(d['LOCATION'], locationNames)
    throw error
  }

  if (now) {
    var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])
    if (dateTime < now) throw new Error(`Mission cannot be in the past.`)
  }

  // Validate roster date range
  if (dateRange) {
    var [startDate, endDate] = dateRange
    var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])
    if (dateTime < startDate || dateTime > endDate)
      throw new Error(
        `Mission must start after 
        ${toLocaleNonAmericanDateString(startDate)} and before
        ${toLocaleNonAmericanDateString(endDate)}.`.replace(/\s+/g, ' '),
      )
  }
}

export const downloadSampleCSV = function({ locations, dateRange }) {
  var exampleWNum = getWeekNumber((dateRange && dateRange[0]) || new Date())
  var exampleDate = dateRange
    ? toLocaleNonAmericanDateString(dateRange[0])
    : '13/06/2019'
  // Grab a random location
  var exampleLocation
  if (locations.length) {
    var exampleIndex = Math.floor(Math.random() * locations.length)
    exampleLocation = locations[exampleIndex].name
  } else {
    exampleLocation = 'Central Station'
  }

  const examplePayAdjustment = ''

  downloadStringAsFile(
    `DATE,START_TIME,LOCATION,PAY_ADJUSTMENT
${exampleDate},08:00,${exampleLocation},${examplePayAdjustment}`,
    `roster-w${exampleWNum}.csv`,
  )
}

/**
 * Calculates whether user can import missions to the current roster
 * @param {number} param.week Roster's week
 * @param {number} param.year Roster's year
 *
 * @returns {boolean} Indicates whether user can import missions to the roster
 */

export const canImport = ({ week, year }) => {
  if (!authorization.isCityManager()) return false
  const startDate = getDateOfISOWeek(week, year)
  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + 7)
  if (endDate < new Date()) return false
  return true
}

export const getRosterTitle = week => `Roster - Week ${week}`

/**
 * (mapStateToProps) Agreggator function that formats data for RosterView component using redux state and component's props
 * @param {object} state Redux State
 * @param {object} props RosterView Props
 *
 * @returns {object} Data for component comsumption
 */
export const formatDataForComponentConsumption = ({
  campaign,
  cityPlan,
  rosterId,
}) => {
  const { missions, city } = cityPlan
  const groupedRosters = groupMissionsByWeek(missions)
  const rosterMissions = groupedRosters[rosterId] || []
  const cityLocations = normalizeGraphQLConnection(city.locations)

  return {
    cityLocations,
    campaign,
    cityPlan,
    city,
    missions: rosterMissions,
    countryCode: city.country.code,
  }
}

/**
 * Generates breadcrumbs for the RosterView page that can be consumed by <BreadcrumbsRenderer />
 *
 * @param {object} param.campaign Roster's campaign data
 * @param {number} param.cityPlanId Roster's city plan ID
 * @param {number} param.campaignId Roster's campaign ID
 * @param {boolean} param.fromCityScreen Indicates whether user come to this page from the city screen
 * @param {object} param.city Roster's city
 *
 * @returns {array} Items that can be consumed by <BreadcrumbsRenderer />
 */
export const getBreadcrumbs = ({
  campaign,
  cityPlanId,
  campaignId,
  fromCityScreen,
  city,
}) => {
  const campaignsPathname = getCampaignsLink()
  const campaignPathname = getCampaignLink(campaignId)
  const cityPlanPathname = getCityPlanLink(campaignId, cityPlanId)

  if (fromCityScreen) {
    return {
      icon: <CitiesIcon style={{ width: 24 }} />,
      items: [
        {
          title: 'Cities',
          pathname: PATHS.main.dashboard + PATHS.dashboard.cities,
        },
        {
          title: city.name,
          pathname:
            PATHS.main.dashboard +
            PATHS.dashboard.cities +
            `/${city.id}/roster`,
        },
        {
          title: 'View a Roster',
          pathname: '/',
        },
      ],
    }
  }

  return {
    icon: <CampaignsIcon style={{ width: 16 }} />,
    items: [
      {
        title: 'Campaigns',
        pathname: campaignsPathname,
      },
      {
        title: campaign.name,
        pathname: campaignPathname,
      },
      {
        title: `${city.name} City Plan`,
        pathname: cityPlanPathname,
      },
      {
        title: 'View a Roster',
        pathname: cityPlanPathname + '/',
      },
    ],
  }
}



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/RosterView/util.js